/*
 * <vh-color-preview> — shows the selected variant's photo over the product cover.
 *
 * Behaviour: click a variant option and the resulting variant's own image covers
 * the main gallery image; click the same option again — or the photo — and the
 * cover comes back. The gallery never contains variant photos itself (see
 * product-media-gallery.liquid), so hiding the overlay is all "deselect" needs.
 * While the overlay is hidden, product-info carries .vh-colorpv-idle and the
 * checked pill is drawn unselected.
 *
 * Inside the quick-add modal none of that applies: the selected variant's photo
 * shows on open, re-tapping keeps it, and the pills keep Dawn's normal look.
 *
 * Three things make this trickier than it looks:
 *
 * 1. A radio that is ALREADY checked fires no `change` event, so the
 *    toggle-off tap is invisible to Dawn's variant machinery. We listen for
 *    `click` instead. Clicking a <label> makes the browser dispatch a second,
 *    synthetic click at the associated input; filtering on
 *    closest('input[type=radio]') means the label's own click resolves to null
 *    and only the synthetic one acts, so a tap toggles exactly once.
 *
 * 2. By the time a radio's click listener runs, the browser has already checked
 *    it, so reading every fieldset's checked input gives the NEW selection —
 *    before Dawn's refetch replaces the picker. That is how Mix & Match resolves
 *    to one First Color / Second Color variant without waiting on the network.
 *
 * 3. product-info.js refetches the section on a variant change and rewrites
 *    parts of the picker, but leaves #GalleryViewer-<section> in place, which is
 *    why the overlay is parented to the viewer: it survives with no re-binding.
 *    Listeners are bound to document once, for the same reason.
 */

if (!customElements.get('vh-color-preview')) {
  customElements.define(
    'vh-color-preview',
    class VhColorPreview extends HTMLElement {
      connectedCallback() {
        if (this.dataset.vhReady) return;
        this.dataset.vhReady = 'true';

        this.variants = this.readVariants();
        this.img = this.querySelector('.vh-colorpv__img');
        if (!this.img || !this.variants.some((variant) => variant.src)) return;

        this.productInfo = this.closest('product-info');
        this.activeId = null;
        this.prefetched = false;

        // Quick-add modal ("Choose options"): the selected variant's photo shows
        // straight away and stays — there is no cover to toggle back to. The
        // tap-again-to-deselect behaviour belongs to the full product page only.
        this.inModal = !!this.closest('quick-add-modal');

        this.adopt();
        this.removeAttribute('hidden');

        if (this.inModal) {
          const picker = this.productInfo && this.productInfo.querySelector('variant-selects');
          const variant = picker && this.findVariant(picker);
          if (variant && variant.src) this.show(variant);
        } else {
          // On the page the selected pill only looks selected while its photo is
          // showing, so "tap again" reads as a real deselect. See the CSS.
          this.setIdle(true);

          // The overlay itself dismisses — otherwise the lightbox would open on a
          // click that visually landed on the variant photo and show the product
          // shot underneath instead, which reads as a bug.
          this.addEventListener('click', () => this.hide());
        }

        this.onDocClick = this.onDocClick.bind(this);
        document.addEventListener('click', this.onDocClick);

        // Warm the photos as soon as the shopper shows intent, so the first swap
        // is instant without costing every visitor the requests on load.
        this.onIntent = this.onIntent.bind(this);
        document.addEventListener('pointerover', this.onIntent);
      }

      disconnectedCallback() {
        if (this.isConnected) return;
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('pointerover', this.onIntent);
      }

      readVariants() {
        const el = this.querySelector('.vh-colorpv__map');
        if (!el) return [];
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return [];
        }
      }

      /* Move the overlay into the gallery viewer so it covers the main image
         only. Falls back to staying put if the viewer can't be found. */
      adopt() {
        const host = document.getElementById(this.dataset.gallery);
        if (!host || host === this.parentElement) return;
        host.classList.add('vh-colorpv-host');
        host.appendChild(this);
      }

      ownsTarget(target) {
        return !this.productInfo || this.productInfo.contains(target);
      }

      onIntent(event) {
        if (this.prefetched) return;
        if (!event.target.closest || !event.target.closest('variant-selects')) return;
        if (!this.ownsTarget(event.target)) return;
        this.prefetched = true;
        document.removeEventListener('pointerover', this.onIntent);
        new Set(this.variants.map((variant) => variant.src).filter(Boolean)).forEach((src) => {
          const img = new Image();
          img.src = src;
        });
      }

      onDocClick(event) {
        if (!event.target.closest) return;

        // Browsing the thumbnails means the shopper wants the product photos.
        const thumbnail = event.target.closest('[id^="GalleryThumbnails"] button');
        if (thumbnail && this.ownsTarget(thumbnail)) {
          this.hide();
          return;
        }

        const input = event.target.closest('input[type="radio"]');
        if (!input) return;
        const picker = input.closest('variant-selects');
        if (!picker || !this.ownsTarget(picker)) return;

        const variant = this.findVariant(picker);
        if (!variant || !variant.src) {
          this.hide();
          return;
        }

        if (!this.inModal && this.activeId === variant.id && this.classList.contains('is-visible')) {
          this.hide();
        } else {
          this.show(variant);
        }
      }

      findVariant(picker) {
        const selected = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown')).map(
          (group) => {
            const checked = group.querySelector('input:checked');
            if (checked) return checked.value;
            const select = group.querySelector('select');
            return select ? select.value : null;
          }
        );
        if (selected.some((value) => value === null)) return null;

        return this.variants.find(
          (variant) =>
            variant.options.length === selected.length &&
            variant.options.every((value, index) => value === selected[index])
        );
      }

      show(variant) {
        this.activeId = variant.id;
        if (this.img.getAttribute('src') !== variant.src) {
          this.img.setAttribute('src', variant.src);
        }
        this.img.alt = variant.alt || '';
        this.classList.add('is-visible');
        this.setIdle(false);
      }

      hide() {
        this.activeId = null;
        this.classList.remove('is-visible');
        this.setIdle(true);
      }

      /* product-info survives the variant refetch, so the class does too. The
         radio stays checked underneath — only its look changes. */
      setIdle(idle) {
        if (this.inModal || !this.productInfo) return;
        this.productInfo.classList.toggle('vh-colorpv-idle', idle);
      }
    }
  );
}
